import { computed, reactive, readonly } from "vue";
import {
  DEFAULT_EQUIPPED,
  DEFAULT_GIMMICK_SLOT,
  celebrationStyle,
  gimmick,
  type CosmeticKind,
} from "../../shared/cosmetics.js";
import type { ShopResponse } from "../../shared/api.js";
import { apiGet, apiPost } from "../utils/api.js";
import { canShowPurchases } from "../utils/nativeApp.js";

/**
 * The player's wardrobe: coins, what they own, and what they are wearing.
 *
 * Unlike every other preference in this app, this one is NOT localStorage.
 * Cosmetics are bought, so the server is the only honest source — a client
 * that could declare its own inventory could dress itself for free. The state
 * here is a cache of what the server said, refreshed on load and after every
 * change, and every mutation goes through an endpoint that re-checks ownership.
 *
 * Signed-out visitors get the defaults and no shop. That is deliberate: a
 * guest identity is a cookie, and selling to a cookie means the purchase dies
 * with the next cache clear.
 */

interface CosmeticsState {
  loaded: boolean;
  loading: boolean;
  /** Whether this deployment runs a shop at all. False hides every trace. */
  enabled: boolean;
  signedIn: boolean;
  coins: number;
  owned: string[];
  equipped: Record<CosmeticKind, string>;
  /** Squad slot the gimmick sits on, e.g. "striker-4". */
  gimmickSlot: string;
  purchasesConfigured: boolean;
  /** Paddle.js settings from the server; null when nothing is on sale here. */
  paddle: { token: string; environment: "sandbox" | "production" } | null;
  error: string;
}

const state = reactive<CosmeticsState>({
  loaded: false,
  loading: false,
  enabled: false,
  signedIn: false,
  coins: 0,
  owned: [],
  equipped: { ...DEFAULT_EQUIPPED },
  gimmickSlot: DEFAULT_GIMMICK_SLOT,
  purchasesConfigured: false,
  paddle: null,
  error: "",
});

/** In-flight refresh, so a route guard and the header do not both fetch. */
let pending: Promise<void> | null = null;

function applyWallet(wallet: ShopResponse["wallet"]) {
  if (!wallet) {
    state.signedIn = false;
    state.coins = 0;
    state.owned = [];
    state.equipped = { ...DEFAULT_EQUIPPED };
    state.gimmickSlot = DEFAULT_GIMMICK_SLOT;
    return;
  }
  state.signedIn = true;
  state.coins = wallet.coins;
  state.owned = wallet.owned;
  state.equipped = { ...DEFAULT_EQUIPPED, ...(wallet.equipped as Record<CosmeticKind, string>) };
  state.gimmickSlot = wallet.equippedSlots?.["gimmick"] ?? DEFAULT_GIMMICK_SLOT;
}

export function useCosmetics() {
  async function refresh(): Promise<void> {
    if (pending) return pending;
    state.loading = true;
    pending = (async () => {
      try {
        const data = await apiGet<ShopResponse>("/api/shop", "Failed to load your items");
        state.enabled = data.enabled !== false;
        applyWallet(state.enabled ? data.wallet : null);
        state.purchasesConfigured = data.purchasesConfigured;
        state.paddle = data.paddle ?? null;
        state.loaded = true;
        state.error = "";
      } catch (e: any) {
        // A wardrobe that fails to load must never block play — the defaults
        // are perfectly playable, so this is recorded and otherwise ignored.
        state.error = e?.message ?? "Failed to load your items";
      } finally {
        state.loading = false;
        pending = null;
      }
    })();
    return pending;
  }

  /**
   * Resolve once the server has been asked at least once. The route guard on
   * /shop needs an answer before it can decide, and it must not fire a second
   * request when the header is already mid-flight.
   */
  async function ensureLoaded(): Promise<void> {
    if (state.loaded) return;
    await refresh();
  }

  /**
   * Wear an item. `slot` picks which player carries a gimmick; leaving it out
   * keeps whoever is wearing it now, so swapping the item does not quietly
   * move it back to the default player.
   */
  async function equip(kind: CosmeticKind, sku: string, slot?: string): Promise<boolean> {
    const previous = state.equipped[kind];
    const previousSlot = state.gimmickSlot;
    state.equipped = { ...state.equipped, [kind]: sku }; // optimistic
    if (kind === "gimmick" && slot) state.gimmickSlot = slot;
    try {
      const data = await apiPost<ShopResponse["wallet"]>(
        "/api/shop/equip",
        slot ? { kind, sku, slot } : { kind, sku },
        "Failed to equip",
      );
      applyWallet(data);
      return true;
    } catch (e: any) {
      state.equipped = { ...state.equipped, [kind]: previous };
      state.gimmickSlot = previousSlot;
      state.error = e?.message ?? "Failed to equip";
      return false;
    }
  }

  async function buy(sku: string): Promise<boolean> {
    try {
      const data = await apiPost<ShopResponse["wallet"]>(
        "/api/shop/purchase",
        { sku },
        "Purchase failed",
      );
      applyWallet(data);
      state.error = "";
      return true;
    } catch (e: any) {
      state.error = e?.message ?? "Purchase failed";
      return false;
    }
  }

  const owns = (sku: string) => state.owned.includes(sku);

  /** The particle recipe currently in force, ready for useGoalCelebration. */
  const activeCelebration = computed(() => celebrationStyle(state.equipped.celebration));
  /** The ornament drawn on one of this player's tokens. */
  const activeGimmick = computed(() => gimmick(state.equipped.gimmick));
  /** Which squad slot carries it. */
  const activeGimmickSlot = computed(() => state.gimmickSlot);

  /** Buying needs a configured provider AND a build that is allowed to sell. */
  const canBuy = computed(() => state.purchasesConfigured && canShowPurchases());
  /** Whether anything shop-shaped may appear in the interface at all. */
  const shopAvailable = computed(() => state.enabled);

  return {
    state: readonly(state),
    refresh,
    ensureLoaded,
    equip,
    buy,
    owns,
    activeCelebration,
    activeGimmick,
    activeGimmickSlot,
    canBuy,
    shopAvailable,
  };
}
